import { Flex, Grid, Text } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { Play, TriangleAlert, Volume2 } from 'lucide-react'
import React, { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { patientInfoButtonBehaviorAtom } from '~/atoms/PatientInfoSettings'
import { PopupPosition, useSettingsState } from '~/atoms/SettingsState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { Alert } from '~/components/core'
import { MangoButton } from '~/components/shared/MangoButton'
import { usePartnerConfig } from '~/hooks/useCurrentPartner'
import { useNotifications } from '~/hooks/useNotifications'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { exampleCalls, exampleTexts } from '~/mocks/popupMockData'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'
import { buildPopupSettingsPayload } from '~/utils/leanPopup'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { Radio, RadioGroup, Toggle } from '@mangovoice/mango-ui'
import { MessageQueueItem, PhoneCall } from '@mangovoice/types'

import { SettingLinkAction, SettingToggle } from './SettingItems'
import { DefaultSettingsWrapper, HelpLink, SubheadingSection } from './Shared'

// Call Pop state management
export interface CallPopState {
  callPopEnabled: boolean
}

const defaultCallPopState: CallPopState = {
  callPopEnabled: isElectron,
}

export const callPopStateAtom = atomWithStorage<CallPopState>(
  StorageKeys.CallPop,
  defaultCallPopState,
)

// PopupPositionSelector component
export const PopupPositionSelector = () => {
  const { t } = useTranslation()
  const titleId = useId()
  const {
    settings: {
      popup: { position },
    },
    setPopupPosition,
  } = useSettingsState()

  const handlePositionChange = (pos: PopupPosition) => {
    setPopupPosition(pos)

    // Notify Electron to reposition the popup window
    if (isElectron && window.electron?.popupWindow?.setPosition) {
      window.electron.popupWindow.setPosition(pos)
    }
  }

  const positionOptions: { value: PopupPosition; label: string }[] = [
    {
      value: 'topLeft',
      label: t('notificationSettings.popupPosition.topLeft'),
    },
    {
      value: 'topRight',
      label: t('notificationSettings.popupPosition.topRight'),
    },
    {
      value: 'bottomLeft',
      label: t('notificationSettings.popupPosition.bottomLeft'),
    },
    {
      value: 'bottomRight',
      label: t('notificationSettings.popupPosition.bottomRight'),
    },
  ]

  return (
    <Flex flexDirection="column" gap={2}>
      <Text
        id={titleId}
        {...textStyles[textStyleKeys.Body2]}
        color="fg.default"
      >
        {t('notificationSettings.popupPosition.title')}
      </Text>
      <RadioGroup
        aria-labelledby={titleId}
        value={position}
        onValueChange={({ value }) => {
          if (value) handlePositionChange(value as PopupPosition)
        }}
        size="sm"
      >
        <Grid templateColumns="repeat(2, 1fr)" gap={2} maxWidth="300px">
          {positionOptions.map(({ value, label }) => (
            <Radio
              key={value}
              value={value}
              padding={3}
              borderRadius="md"
              border="2px solid"
              borderColor="border.default"
              bg="bg.panel"
              color="fg.muted"
              cursor="pointer"
              fontSize="xs"
              transition="all 0.2s"
              _hover={{ bg: 'bg.emphasized' }}
              _checked={{
                bg: 'brand.subtle',
                borderColor: 'brand.muted',
                color: 'fg.default',
                _hover: { bg: 'brand.subtle', borderColor: 'border.primary' },
              }}
            >
              {label}
            </Radio>
          ))}
        </Grid>
      </RadioGroup>
    </Flex>
  )
}

export const NotificationSettings = () => {
  const { t } = useTranslation()
  const {
    settings: {
      notifications: { hasNewTextSound },
    },
    setHasNewTextSound,
  } = useSettingsState()

  const { hasGranted, sendSampleNotification } = useNotifications()

  // Call Pop state management
  const [callPopState, setCallPopState] = useAtom(callPopStateAtom)
  const [patientInfoBehavior, setPatientInfoBehavior] = useAtom(
    patientInfoButtonBehaviorAtom,
  )
  const partnerConfig = usePartnerConfig()
  const [, setMessageQueue] = useSharedVariable<MessageQueueItem[]>(
    'MessageQueue',
    [],
  )
  const [, setCallList] = useSharedVariable<PhoneCall[]>('CallList', [])

  return (
    <DefaultSettingsWrapper>
      {!isElectron && !hasGranted ? (
        <Alert
          icon={
            <TriangleAlert size={24} color="var(--chakra-colors-red-solid)" />
          }
        >
          {t('notificationSettings.alertMessage')}
        </Alert>
      ) : null}
      <SubheadingSection id="sounds">
        {!isElectron && (
          <Text {...textStyles[textStyleKeys.Body2_Bold]} color="fg.default">
            {t('notificationSettings.browserNotifications')}
          </Text>
        )}
        <SettingToggle
          icon={Volume2}
          label={t('notificationSettings.playSoundForNewTexts')}
          onClick={() => setHasNewTextSound(!hasNewTextSound)}
          isChecked={hasNewTextSound}
          disabled={!isElectron && !hasGranted}
        />
        {!isElectron && hasGranted && (
          <SettingLinkAction
            icon={Play}
            label={t('notificationSettings.testNotification')}
            onClick={sendSampleNotification}
          />
        )}
        <Text {...textStyles[textStyleKeys.Body3]} color="fg.default">
          {t('notificationSettings.volumeControl')}
        </Text>
        {!isElectron && (
          <>
            <Text {...textStyles[textStyleKeys.Body2_Bold]} color="fg.default">
              {t('notificationSettings.help')}
            </Text>

            <Text {...textStyles[textStyleKeys.Body3]} color="fg.default">
              <HelpLink
                color="fg.info"
                href="https://support.mangovoice.com/how-do-i-turn-on-off-notifications-on-the-web-app-windows-instructions/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('notificationSettings.helpLinkText')}
              </HelpLink>{' '}
              {t('notificationSettings.onGoogleChrome')}
            </Text>
          </>
        )}
      </SubheadingSection>

      {/* Call Pop Settings Section */}
      {isElectron && (
        <SubheadingSection id="call-pop">
          <Flex alignItems={'center'}>
            <Text {...textStyles[textStyleKeys.Body2_Bold]} color="fg.default">
              {t('notificationSettings.callPop.title')}
            </Text>
          </Flex>
          <SettingToggle
            label={t('notificationSettings.callPop.description')}
            isChecked={callPopState.callPopEnabled}
            onClick={() => {
              const newState = !callPopState.callPopEnabled

              // Update state first
              setCallPopState((prev) => ({
                ...prev,
                callPopEnabled: newState,
              }))

              if (isElectron) {
                // Small delay to ensure state update completes before sending IPC message
                setTimeout(() => {
                  try {
                    window.electron?.popupWindow.sendCallPopSetting(
                      buildPopupSettingsPayload(newState),
                    )
                  } catch (error) {
                    console.error(error)
                  }
                }, 50)
              }
            }}
          />

          {callPopState.callPopEnabled && (
            <Flex flexDirection="column" gap={4}>
              {!!partnerConfig.deepLinkPattern?.trim() && (
                <Flex alignItems="center" gap={2}>
                  <Toggle
                    id="patient-info-behavior"
                    checked={patientInfoBehavior === 'answer-then-deeplink'}
                    onCheckedChange={(details: { checked: boolean }) =>
                      setPatientInfoBehavior(
                        details.checked
                          ? 'answer-then-deeplink'
                          : 'deeplink-only',
                      )
                    }
                    size="md"
                  >
                    {t(
                      'notificationSettings.callPop.answerOnPatientInfoButtonClick',
                    )}
                  </Toggle>
                </Flex>
              )}
              <PopupPositionSelector />
              <Flex alignItems={'center'}>
                <MangoButton
                  size={Size.sm}
                  disabled={!callPopState.callPopEnabled}
                  onClick={() => {
                    if (callPopState.callPopEnabled) {
                      setMessageQueue(exampleTexts as MessageQueueItem[])
                      setCallList(exampleCalls)
                    }
                  }}
                >
                  {t('notificationSettings.callPop.showExampleButton')}
                </MangoButton>
                <Text
                  color="var(--chakra-colors-primary)"
                  opacity={callPopState.callPopEnabled ? 0 : 1}
                  marginLeft={2}
                  fontSize="sm"
                  aria-disabled={callPopState.callPopEnabled}
                >
                  {t('notificationSettings.callPop.enableExampleHint')}
                </Text>
              </Flex>
            </Flex>
          )}
        </SubheadingSection>
      )}
    </DefaultSettingsWrapper>
  )
}
